import React, { useState, useEffect, useContext } from 'react';
import useBreadcrumbs from '../../../utils/hooks/useBreadcrumbs';
import TitlePage from '../../../UI/TitlePage/TitlePage';
import Button from '../../../UI/Button/Button';
import Input from '../../../UI/Input/Input';
import SurveyAPI from '../../../services/api/SurveyService';
import { NotifyContext } from '../../../context/notifyContext';

interface Statistics {
  total_surveys_sent: number;
  completed_surveys: number;
  pending_surveys: number;
  completion_rate: number;
  average_ratings_per_question: Array<{
    question_id: number;
    question_text_fr: string;
    question_text_en: string;
    average_rating: number;
    response_count: number;
  }>;
  response_count_over_time: Array<{
    date: string;
    count: number;
  }>;
}

interface TutorComparison {
  tutor_id: number;
  tutor_name: string;
  average_rating: number;
  response_count: number;
  total_answers: number;
}

interface Trends {
  completion_trends: Array<{
    period: string;
    total_sent: number;
    completed: number;
    completion_rate: number;
  }>;
  rating_trends: Array<{
    period: string;
    average_rating: number;
  }>;
}

const SurveyAnalytics: React.FC = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [tutorComparison, setTutorComparison] = useState<TutorComparison[]>([]);
  const [trends, setTrends] = useState<Trends | null>(null);
  const [loading, setLoading] = useState(true);

  const notify = useContext(NotifyContext);

  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    tutor_id: '',
    question_id: '',
    group_by: 'day' as 'day' | 'week' | 'month',
  });

  useBreadcrumbs([
    { label: 'Tableau de bord', is_link: true, href: '/app/dashboard' },
    { label: 'Gestion des sondages', is_link: true, href: '/app/surveys' },
    { label: 'Analytics & Rapports', is_link: false },
  ]);

  useEffect(() => {
    fetchAllData();
  }, [filters]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchStatistics(),
        fetchTutorComparison(),
        fetchTrends(),
      ]);
    } catch (err: any) {
      notify('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response: any = await SurveyAPI.getSurveyStatistics({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        tutor_id: filters.tutor_id ? Number(filters.tutor_id) : undefined,
      });

      if (response && response.data) {
        setStatistics(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
    }
  };

  const fetchTutorComparison = async () => {
    try {
      const response: any = await SurveyAPI.getTutorComparison({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      });

      if (response && response.data) {
        setTutorComparison(response.data.tutor_comparison || []);
      }
    } catch (err: any) {
      console.error('Error fetching tutor comparison:', err);
    }
  };

  const fetchTrends = async () => {
    try {
      const response: any = await SurveyAPI.getTrends({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        question_id: filters.question_id ? Number(filters.question_id) : undefined,
        group_by: filters.group_by,
      });

      if (response && response.data) {
        setTrends(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching trends:', err);
    }
  };

  const handleExport = async () => {
    try {
      const response = await SurveyAPI.exportResponses({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        tutor_id: filters.tutor_id ? Number(filters.tutor_id) : undefined,
      });

      if (response && response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `survey_analytics_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        notify('Export réussi', 'success');
      }
    } catch (err: any) {
      notify(err.message || 'Erreur lors de l\'export', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TitlePage>
        <div className="flex justify-between items-center">
          <span>Analytics & Rapports</span>
          <Button
            onClick={handleExport}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            Exporter les données
          </Button>
        </div>
      </TitlePage>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <h3 className="font-semibold text-gray-700">Filtres</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début
            </label>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin
            </label>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Tuteur
            </label>
            <Input
              type="number"
              value={filters.tutor_id}
              onChange={(e) => setFilters((prev) => ({ ...prev, tutor_id: e.target.value }))}
              placeholder="Filtrer par tuteur"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Regroupement
            </label>
            <select
              value={filters.group_by}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  group_by: e.target.value as 'day' | 'week' | 'month',
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="day">Par jour</option>
              <option value="week">Par semaine</option>
              <option value="month">Par mois</option>
            </select>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Sondages envoyés</h3>
            <p className="text-3xl font-bold text-gray-800">{statistics.total_surveys_sent}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Complétés</h3>
            <p className="text-3xl font-bold text-green-600">{statistics.completed_surveys}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">En attente</h3>
            <p className="text-3xl font-bold text-yellow-600">{statistics.pending_surveys}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Taux de complétion</h3>
            <p className="text-3xl font-bold text-blue-600">{statistics.completion_rate.toFixed(1)}%</p>
          </div>
        </div>
      )}

      {/* Average Ratings Per Question */}
      {statistics && statistics.average_ratings_per_question.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            Moyennes par Question (Notes)
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question (FR)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Question (EN)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Moyenne
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de réponses
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {statistics.average_ratings_per_question.map((item) => (
                  <tr key={item.question_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-md truncate">
                      {item.question_text_fr}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-md truncate">
                      {item.question_text_en}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <span>{item.average_rating.toFixed(2)}</span>
                        <div className="flex">
                          {Array.from({ length: 5 }, (_, index) => {
                            const starValue = index + 1;
                            const isFilled = starValue <= item.average_rating;
                            return (
                              <svg
                                key={starValue}
                                className={`w-4 h-4 ${isFilled ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.response_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tutor Comparison */}
      {tutorComparison.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            Comparaison des Tuteurs
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tuteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note moyenne
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre de sondages
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total de réponses
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tutorComparison.map((tutor) => (
                  <tr key={tutor.tutor_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {tutor.tutor_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{tutor.average_rating.toFixed(2)}</span>
                        <div className="flex">
                          {Array.from({ length: 5 }, (_, index) => {
                            const starValue = index + 1;
                            const isFilled = starValue <= tutor.average_rating;
                            return (
                              <svg
                                key={starValue}
                                className={`w-4 h-4 ${isFilled ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tutor.response_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tutor.total_answers}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Trends */}
      {trends && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Completion Trends */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">
              Tendances de Complétion
            </h2>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {trends.completion_trends.map((trend, index) => (
                <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{trend.period}</span>
                    <span className="text-sm text-gray-500">
                      {trend.completed} / {trend.total_sent}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 mb-1">
                    <div
                      className="bg-blue-500 h-4 rounded-full transition-all"
                      style={{ width: `${trend.completion_rate}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Taux: {trend.completion_rate.toFixed(1)}%
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Rating Trends */}
          {trends.rating_trends.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-6">
                Tendances des Notes
              </h2>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {trends.rating_trends.map((trend, index) => (
                  <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">{trend.period}</span>
                      <span className="text-sm font-semibold text-gray-800">
                        {trend.average_rating.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {Array.from({ length: 5 }, (_, starIndex) => {
                          const starValue = starIndex + 1;
                          const isFilled = starValue <= trend.average_rating;
                          return (
                            <svg
                              key={starValue}
                              className={`w-4 h-4 ${isFilled ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          );
                        })}
                      </div>
                      <div
                        className="flex-1 bg-gray-200 rounded-full h-3"
                        style={{
                          background: `linear-gradient(to right, #fbbf24 0%, #fbbf24 ${(trend.average_rating / 5) * 100}%, #e5e7eb ${(trend.average_rating / 5) * 100}%, #e5e7eb 100%)`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyAnalytics;
